/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { useState } from 'react'
import colors from '@sportninja/common/constants/appColors'
import { font, weightBold, weightRegular } from 'src/components/css'
import TeamBillingTable from 'src/components/List/TeamBillingTable'
import {
  registrationSeasonDeepLinkFromSearch,
  registrationSeasonSearchParams,
  resolveRegistrationSeason,
} from '@sportninja/common/features/team-balance/utils/registrationSeasons'
import type { RegistrationSeasonDeepLink } from '@sportninja/common/features/team-balance/utils/registrationSeasons'
import TeamPlayerFinderPanel from 'src/features/team/playerFinder/components/TeamPlayerFinderPanel'
import { useUrlManager } from 'src/hooks/useUrlManager'
import BalanceTab from './BalanceTab'
import RegistrationScopeField from './components/RegistrationScopeField'
import RegistrationSectionHeader from './components/RegistrationSectionHeader'
import { SCOPE_CONTROL_HEIGHT } from './constants/registrationScopeRow'
import RegistrationSeasonPicker from './components/RegistrationSeasonPicker'
import { useRegistrationSeasons } from './hooks/useRegistrationSeasons'
import { balanceLabel } from './utils/balanceI18n'
import {
  RegistrationSubTab,
  registrationSubTabFromSearch,
  registrationSubTabs,
  resolveRegistrationSubTab,
} from './utils/registrationSubTabs'

interface RegistrationTabProps {
  teamId: string
  /** Keys the per-caller roster-discovery cache shared with the page probe. */
  authUserId?: string | number | null
  /** Feed `post` ability — hides "Post & Notify Team" in the Command Center. */
  canPost?: boolean
  /**
   * The former `showBilling` gate (`!isCanlan && permission.update`). The Billing
   * sub-tab is shown — and editable — exactly when this is true.
   */
  canEditBilling: boolean
  /**
   * The former `showBalance` probe gate. The Command Center sub-tab (the balance
   * sheet) is shown when this is true.
   */
  showCommandCenter: boolean
  /**
   * SN-4709 × SN-8081 — the Player Finder settings probe gate. The Player Finder
   * sub-tab is shown when this is true, Team-Managed Budget or not. Optional so a
   * caller that predates the sub-tab simply does not get it.
   */
  showPlayerFinder?: boolean
  /**
   * Team sport + roster-edit rights, forwarded to the Command Center so its kebab
   * can open the roster Edit Player editor. Team-level, so always available.
   */
  sportId?: string | null
  sportName?: string
  canManageRoster?: boolean
}

/**
 * SN-4709 follow-up (Jeff) — the Team page's "Registration" tab. Folds the former
 * standalone Billing tab in as a sub-tab beneath the roster/registration "Command
 * Center" (the balance sheet). Sub-tab visibility reuses the two former top-level
 * gates verbatim: Command Center on the balance probe (`showCommandCenter`),
 * Billing on `!isCanlan && permission.update` (`canEditBilling`) — plus, since
 * SN-8081, Player Finder on its own probe (`showPlayerFinder`). The Team page
 * only mounts this when at least one is visible; when exactly one is, the switcher
 * is hidden and that sub-tab renders alone — byte-identical to the old single tab.
 *
 * The segmented switcher mirrors the Roster tab's Players/Team Officials control
 * (UnifiedRosterPage) — local state, no URL change — rather than antd Tabs.
 */
// Only an explicit `?tab=billing` (a redirected /billing bookmark) or
// `?tab=player-finder` deep link pre-selects a sub-tab. Absent that, the active
// sub-tab follows the gate-derived default, which prefers the Command Center.
const initialUserChoice = (): RegistrationSubTab | null =>
  typeof window !== 'undefined'
    ? registrationSubTabFromSearch(window.location.search)
    : null

// A `?roster=` (Team Budget notification) or `?sn_schedule=` (Billing roster
// picker) link pre-selects the season, the way each sub-tab's own picker did.
const initialSeasonDeepLink = (): RegistrationSeasonDeepLink =>
  typeof window !== 'undefined'
    ? registrationSeasonDeepLinkFromSearch(window.location.search)
    : { rosterId: null, scheduleUid: null }

const SUB_TAB_LABEL_KEYS: Record<RegistrationSubTab, string> = {
  'command-center': 'common:commandCenter',
  billing: 'common:billing',
  'player-finder': 'common:teamPlayerFinderTab',
}

const RegistrationTab = ({
  teamId,
  authUserId = null,
  canPost = false,
  canEditBilling,
  showCommandCenter,
  showPlayerFinder = false,
  sportId = null,
  sportName,
  canManageRoster = false,
}: RegistrationTabProps) => {
  const subTabs = registrationSubTabs({
    showCommandCenter,
    canEditBilling,
    showPlayerFinder,
  })

  // The user's explicit pick (a click, or a ?tab= deep link). Null means "follow
  // the default", which prefers the Command Center.
  const [userChoice, setUserChoice] = useState<RegistrationSubTab | null>(
    initialUserChoice
  )

  /**
   * The team registration the Player Finder sub-tab should open on — set when
   * the Team Budget sheet links a rep there for one of its registrations.
   */
  const [finderRegistrationId, setFinderRegistrationId] = useState<
    string | null
  >(null)

  /**
   * The element the Player Finder sub-tab portals its competition picker into. A
   * state ref rather than useRef: the child has to RE-RENDER once the node
   * exists, and a ref mutation does not schedule one.
   */
  const [competitionSlot, setCompetitionSlot] = useState<HTMLDivElement | null>(
    null
  )

  /**
   * The season the user picked — or carried across a sub-tab switch. Null means
   * "follow the default": a deep link, else the active sub-tab's own default.
   */
  const [seasonChoice, setSeasonChoice] = useState<string | null>(null)
  const [seasonDeepLink] = useState(initialSeasonDeepLink)

  const showSwitcher = subTabs.length > 1
  // Command Center is the default whenever it is visible, then Billing, then
  // Player Finder. Derived from the CURRENT gates on every render, NOT frozen at
  // mount — the probes resolve ASYNC and often AFTER Billing has already
  // rendered, so freezing the initial value stranded the tab on Billing. A gate
  // can also flip off under the active selection, so an out-of-set userChoice
  // falls back to the default too.
  const effectiveSubTab = resolveRegistrationSubTab(subTabs, userChoice)

  /**
   * One season scopes every sub-tab. It is a roster id — a roster is the team in
   * one season, and all three sub-tabs are roster-keyed — offered from the union
   * of the visible sub-tabs' sources, so it survives a switch to a sub-tab that
   * has nothing for it (that sub-tab says so instead).
   *
   * Absent a pick, it is not resolved until the ACTIVE sub-tab's source has
   * loaded: resolving early against another source would land on that source's
   * newest season, then jump when the active one arrived.
   */
  const { seasons, seasonIdsBySubTab, isLoadingBySubTab } =
    useRegistrationSeasons({ teamId, authUserId, subTabs })
  const isLoadingActiveSeasons = isLoadingBySubTab[effectiveSubTab]
  const seasonId =
    isLoadingActiveSeasons && seasonChoice === null
      ? null
      : resolveRegistrationSeason({
          seasons,
          userChoice: seasonChoice,
          deepLink: seasonDeepLink,
          activeSubTabSeasonIds: seasonIdsBySubTab[effectiveSubTab],
        })
  const showSeasonPicker = isLoadingActiveSeasons || seasons.length > 0

  // Switching sections carries the season on screen into the next one, even
  // when it was only the default — the section changes, the season does not.
  const { setParams } = useUrlManager()

  // A pick is written to the address bar (`sn_schedule`), so a reload reopens
  // the season — the way Billing's own roster picker did. A section switch is
  // not a pick, so it writes nothing.
  const selectSeason = (id: string) => {
    setSeasonChoice(id)
    setParams(
      registrationSeasonSearchParams(seasons.find((season) => season.id === id))
    )
  }

  const selectSubTab = (tab: RegistrationSubTab) => {
    if (seasonId !== null) {
      setSeasonChoice(seasonId)
    }
    setUserChoice(tab)
  }

  const openPlayerFinder = (teamRegistrationId: string) => {
    setFinderRegistrationId(teamRegistrationId)
    selectSubTab('player-finder')
  }

  return (
    <div css={containerStyle}>
      {/*
        The tab's SCOPE row: the Season picker and the Section (sub-tab)
        switcher, each labelled, then — on Player Finder — the competition
        picker, portalled in from the panel that owns its selection.

        The season governs every section, so it lives here rather than inside
        each one: switching sections keeps the season, and the section renders
        that season's content (or says it has none).
      */}
      {showSeasonPicker ||
      showSwitcher ||
      effectiveSubTab === 'player-finder' ? (
        <div css={scopeRowStyle}>
          {showSeasonPicker ? (
            <RegistrationSeasonPicker
              seasons={seasons}
              selectedSeasonId={seasonId}
              onSelect={selectSeason}
              isLoading={isLoadingActiveSeasons}
            />
          ) : null}
          {showSwitcher ? (
            <RegistrationScopeField
              label={balanceLabel('common:registrationSection')}
            >
              <div css={switcherStyle}>
                {subTabs.map((tab) => {
                  const isActive = effectiveSubTab === tab
                  return (
                    <button
                      key={tab}
                      type='button'
                      onClick={() => selectSubTab(tab)}
                      css={buttonStyle(isActive)}
                    >
                      {balanceLabel(SUB_TAB_LABEL_KEYS[tab])}
                    </button>
                  )
                })}
              </div>
            </RegistrationScopeField>
          ) : null}
          {effectiveSubTab === 'player-finder' ? (
            <div
              ref={setCompetitionSlot}
              css={competitionSlotStyle}
              data-testid='registration-competition-slot'
            />
          ) : null}
        </div>
      ) : null}

      <RegistrationSectionHeader subTab={effectiveSubTab} />

      {effectiveSubTab === 'billing' ? (
        <TeamBillingTable
          canEdit={canEditBilling}
          teamId={teamId}
          rosterId={seasonId}
        />
      ) : effectiveSubTab === 'player-finder' ? (
        <TeamPlayerFinderPanel
          teamId={teamId}
          authUserId={authUserId}
          rosterId={seasonId}
          preferredRegistrationId={finderRegistrationId}
          pickerSlot={competitionSlot}
          onOpenTeamBudget={
            showCommandCenter ? () => selectSubTab('command-center') : undefined
          }
        />
      ) : (
        <BalanceTab
          teamId={teamId}
          authUserId={authUserId}
          canPost={canPost}
          sportId={sportId}
          sportName={sportName}
          canManageRoster={canManageRoster}
          rosterId={seasonId}
          onOpenPlayerFinder={showPlayerFinder ? openPlayerFinder : undefined}
        />
      )}
    </div>
  )
}

export default RegistrationTab

const containerStyle = css`
  display: flex;
  flex-direction: column;
`

/**
 * The scope row. Bottom-aligned so the labelled fields' controls line up with the
 * unlabelled competition picker.
 */
const scopeRowStyle = css`
  display: flex;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 16px;
`

// The Season select's height; the buttons stretch to fill it inside the padding.
const switcherStyle = css`
  display: inline-flex;
  align-items: stretch;
  gap: 2px;
  background-color: transparent;
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  padding: 4px;
  height: ${SCOPE_CONTROL_HEIGHT}px;
`

/** Right-aligned, centred on the controls beside it. */
const competitionSlotStyle = css`
  margin-left: auto;
  display: flex;
  align-items: center;
  min-height: ${SCOPE_CONTROL_HEIGHT}px;
  min-width: 0;
`

const buttonStyle = (isActive: boolean) => css`
  ${font.body}
  ${isActive ? weightBold : weightRegular}
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 128px;
  padding: 0 12px;
  border-radius: 6px;
  background-color: ${isActive ? colors.SECONDARY_300 : 'transparent'};
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  line-height: 20.3px;
  color: ${colors.NEUTRAL_0};
  white-space: nowrap;

  &:hover {
    background-color: ${isActive ? colors.SECONDARY_300 : colors.SECONDARY_200};
  }

  &:active {
    transform: scale(0.98);
  }
`
